// The organisation's pictures for the `image` widget (VisionPlatform dashboard-images, vision-api 0.62.0): listImages,
// uploadImage, getImage and deleteImage. A picture is fetched with the person's token and shown from a blob URL, since
// tokens live in memory and an <img src> cannot carry them (IMG-09).
import { queryOptions } from "@tanstack/react-query";
import { apiClient, orgHeader, unwrap } from "../../api/client";
import type { components } from "../../api/generated/vision-api";
import { ApiError, NetworkError, toApiError } from "../../api/problem";

type T = (key: string, options?: Record<string, unknown>) => string;

export type OrgImage = components["schemas"]["OrgImage"];

/** IMG-02 and IMG-05, said before the upload. */
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const IMAGE_TYPES = ["image/jpeg", "image/png"] as const;
export const IMAGE_LIMIT = 100;

const api = () => apiClient("customers");

export const imagesKey = (orgId: string) => ["org", orgId, "/v1/images"] as const;

/** Every picture of the organisation, for the picker: at most 100 (IMG-05), so one page. */
export const imagesQuery = (orgId: string) =>
  queryOptions({
    queryKey: [...imagesKey(orgId), "list"],
    queryFn: () =>
      unwrap(api().GET("/v1/images", { params: { query: { limit: IMAGE_LIMIT }, header: orgHeader(orgId) } })),
  });

/** One picture's bytes. Kept a day, as the server's Cache-Control says. */
export const imageBlobQuery = (orgId: string, imageId: string) =>
  queryOptions({
    queryKey: [...imagesKey(orgId), imageId, "blob"],
    queryFn: async () => {
      const { data, error, response } = await api().GET("/v1/images/{image_id}", {
        params: { path: { image_id: imageId }, header: orgHeader(orgId) },
        parseAs: "blob",
      });
      if (!response.ok) throw toApiError(response.status, error, response.headers.get("Retry-After"));
      return data as unknown as Blob;
    },
    staleTime: 24 * 60 * 60 * 1000,
    retry: (n, e) => !(e instanceof ApiError && (e.status === 404 || e.status === 403)) && n < 2,
  });

// One object URL per fetched picture, made the first time it is drawn and kept as long as the page: the query cache
// holds the bytes anyway (a day, at most 100 pictures), and a URL revoked under a mounted <img> would break it.
const urls = new WeakMap<Blob, string>();

/** The object URL of a fetched picture. */
export function pictureUrl(blob: Blob): string {
  let u = urls.get(blob);
  if (!u) {
    u = URL.createObjectURL(blob);
    urls.set(blob, u);
  }
  return u;
}

/** What is wrong with a file before it is sent, as an i18n key, or undefined. */
export function fileProblem(f: Pick<File, "size" | "type">): string | undefined {
  if (!(IMAGE_TYPES as readonly string[]).includes(f.type)) return "images.error.unsupported_type";
  if (f.size > IMAGE_MAX_BYTES) return "images.error.too_large";
  return undefined;
}

export async function uploadImage(orgId: string, file: File): Promise<OrgImage> {
  try {
    return await unwrap(
      api().POST("/v1/images", {
        params: { query: { name: file.name.slice(0, 200) || "picture" }, header: orgHeader(orgId) },
        body: file as unknown as string,
        bodySerializer: (b: unknown) => b as BodyInit,
        headers: { "Content-Type": file.type },
      }),
    );
  } catch (e) {
    if (e instanceof NetworkError || e instanceof ApiError) throw e;
    throw new NetworkError(e);
  }
}

export function deleteImage(orgId: string, imageId: string) {
  return unwrap(
    api().DELETE("/v1/images/{image_id}", { params: { path: { image_id: imageId }, header: orgHeader(orgId) } }),
  );
}

const CODES = ["too_large", "unsupported_type", "not_an_image", "too_many_pixels", "image_limit"];

export function imageError(e: unknown, t: T): string {
  if (!(e instanceof ApiError)) return t("images.error.unknown");
  if (e.code && CODES.includes(e.code)) return t(`images.error.${e.code}`);
  if (e.status === 413) return t("images.error.too_large");
  if (e.status === 403) return t("images.error.forbidden");
  return e.message || t("images.error.unknown");
}

/** The picture's config of an `image` widget, when it has one. */
export function imageConfig(config: Record<string, unknown> | undefined): { asset: string; alt: string } | undefined {
  const asset = config?.asset;
  const alt = config?.alt;
  return typeof asset === "string" && asset ? { asset, alt: typeof alt === "string" ? alt : "" } : undefined;
}

/** A note's text, as the `text` widget keeps it (config_text: 1 to 2000 characters, plain). */
export function noteConfig(text: string): { text: string } | undefined {
  const t = text.trim();
  return t && t.length <= 2000 ? { text: t } : undefined;
}
